import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import AttachmentsWidget from '../components/AttachmentsWidget';

const STAGES = [
  { key: 'nominated',   label: 'Nominiert',    color: '#3b82f6' },
  { key: 'confirmed',   label: 'Bestätigt',    color: '#8b5cf6' },
  { key: 'arrived',     label: 'Angekommen',   color: '#f59e0b' },
  { key: 'loading',     label: 'Beladung',     color: '#f97316' },
  { key: 'loaded',      label: 'Beladen',      color: '#22c55e' },
  { key: 'sailing',     label: 'Unterwegs',    color: '#06b6d4' },
  { key: 'discharged',  label: 'Entladen',     color: '#10b981' },
  { key: 'completed',   label: 'Abgeschlossen',color: '#059669' },
  { key: 'cancelled',   label: 'Storniert',    color: '#ef4444' },
];

const TYPES = ['tanker', 'bulk_carrier', 'lng', 'lpg', 'container', 'other'];

const EMPTY = {
  vessel_name: '', imo_number: '', flag: '', vessel_type: '', dwt: '',
  charterer: '', deal_reference: '',
  port_of_loading: '', port_of_discharge: '',
  eta_loading: '', etd_loading: '', eta_discharge: '',
  laycan_start: '', laycan_end: '',
  laytime_allowed_hrs: '', laytime_used_hrs: '', demurrage_rate: '',
  stage: 'nominated', notes: '',
};

function stageMeta(k) { return STAGES.find(s => s.key === k) || STAGES[0]; }

function StageBadge({ stage }) {
  const m = stageMeta(stage);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 10px',
      borderRadius: 99, fontSize: 11, fontWeight: 700,
      background: m.color + '22', color: m.color }}>
      {m.label}
    </span>
  );
}

function VesselModal({ vessel, onClose, onSaved }) {
  const [form, setForm] = useState(vessel ? { ...vessel } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.vessel_name.trim()) { setErr('Schiffsname ist erforderlich'); return; }
    setSaving(true); setErr('');
    try {
      if (vessel) await API.put(`/vessels/${vessel.id}`, form);
      else        await API.post('/vessels', form);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 760 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">{vessel ? 'Vessel bearbeiten' : 'Vessel nominieren'}</div>
        {err && <div className="error-msg">{err}</div>}

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Schiffsname *</label>
            <input className="form-input" value={form.vessel_name} onChange={e => set('vessel_name', e.target.value)} placeholder="z.B. ENERGY TRADER I" />
          </div>
          <div className="form-group">
            <label className="form-label">IMO-Nummer</label>
            <input className="form-input" value={form.imo_number} onChange={e => set('imo_number', e.target.value)} placeholder="z.B. 9321483" />
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Flagge</label>
            <input className="form-input" value={form.flag} onChange={e => set('flag', e.target.value)} placeholder="z.B. Panama" />
          </div>
          <div className="form-group">
            <label className="form-label">Schiffstyp</label>
            <select className="form-input" value={form.vessel_type} onChange={e => set('vessel_type', e.target.value)}>
              <option value="">— wählen —</option>
              {TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ').toUpperCase()}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">DWT</label>
            <input className="form-input" value={form.dwt} onChange={e => set('dwt', e.target.value)} placeholder="z.B. 105,000 MT" />
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Charterer</label>
            <input className="form-input" value={form.charterer} onChange={e => set('charterer', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Deal-Referenz</label>
            <input className="form-input" value={form.deal_reference} onChange={e => set('deal_reference', e.target.value)} placeholder="z.B. DEAL-0001" />
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Port of Loading</label>
            <input className="form-input" value={form.port_of_loading} onChange={e => set('port_of_loading', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Port of Discharge</label>
            <input className="form-input" value={form.port_of_discharge} onChange={e => set('port_of_discharge', e.target.value)} />
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Laycan Start</label>
            <input type="date" className="form-input" value={form.laycan_start} onChange={e => set('laycan_start', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Laycan End</label>
            <input type="date" className="form-input" value={form.laycan_end} onChange={e => set('laycan_end', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">ETA Loading</label>
            <input type="date" className="form-input" value={form.eta_loading} onChange={e => set('eta_loading', e.target.value)} />
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">ETD Loading</label>
            <input type="date" className="form-input" value={form.etd_loading} onChange={e => set('etd_loading', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">ETA Discharge</label>
            <input type="date" className="form-input" value={form.eta_discharge} onChange={e => set('eta_discharge', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Stage</label>
            <select className="form-input" value={form.stage} onChange={e => set('stage', e.target.value)}>
              {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Laytime erlaubt (h)</label>
            <input className="form-input" value={form.laytime_allowed_hrs} onChange={e => set('laytime_allowed_hrs', e.target.value)} placeholder="z.B. 72" />
          </div>
          <div className="form-group">
            <label className="form-label">Laytime genutzt (h)</label>
            <input className="form-input" value={form.laytime_used_hrs} onChange={e => set('laytime_used_hrs', e.target.value)} placeholder="z.B. 84.5" />
          </div>
          <div className="form-group">
            <label className="form-label">Demurrage Rate (USD/Tag)</label>
            <input className="form-input" value={form.demurrage_rate} onChange={e => set('demurrage_rate', e.target.value)} placeholder="z.B. 25,000" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Notizen</label>
          <textarea className="form-input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

function VesselDetail({ vessel: init, onBack, onEdit, onDelete }) {
  const [vessel, setVessel] = useState(init);
  const [tab, setTab] = useState('info');

  const reload = async () => {
    const r = await API.get(`/vessels/${init.id}`);
    setVessel(r.data);
  };

  const updateStage = async (stage) => {
    await API.put(`/vessels/${vessel.id}`, { ...vessel, stage });
    reload();
  };

  const PIPELINE = STAGES.filter(s => !['cancelled'].includes(s.key));
  const stageIdx = PIPELINE.findIndex(s => s.key === vessel.stage);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Zurück</button>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>⛴ {vessel.vessel_name}</h1>
        <StageBadge stage={vessel.stage} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onEdit}>Bearbeiten</button>
          <button className="btn btn-danger btn-sm" onClick={onDelete}>Löschen</button>
        </div>
      </div>

      {/* Pipeline */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Voyage Progress</div>
        <div style={{ display: 'flex', overflowX: 'auto', gap: 0 }}>
          {PIPELINE.map((s, i) => {
            const active = s.key === vessel.stage;
            const done   = i < stageIdx;
            const color  = done ? '#22c55e' : active ? s.color : 'var(--border)';
            return (
              <div key={s.key} onClick={() => updateStage(s.key)}
                style={{ flex: 1, minWidth: 80, textAlign: 'center', cursor: 'pointer' }}>
                <div style={{ height: 4, background: i === 0 ? 'transparent' : done || active ? '#22c55e' : 'var(--border)', marginBottom: 8 }} />
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: color,
                  margin: '0 auto 4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: done || active ? '#000' : 'var(--text3)' }}>
                  {done ? '✓' : i + 1}
                </div>
                <div style={{ fontSize: 10, color: active ? s.color : done ? '#22c55e' : 'var(--text3)', fontWeight: active ? 700 : 400 }}>
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
        {[{ id:'info',label:'Info' },{ id:'laytime',label:'Laytime' },{ id:'attachments',label:'Anhänge' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className="btn btn-ghost btn-sm"
            style={{ borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              borderRadius: '6px 6px 0 0', color: tab === t.id ? 'var(--accent)' : 'var(--text2)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title">Schiffsdaten</div>
            {[
              ['IMO', vessel.imo_number], ['Flagge', vessel.flag],
              ['Typ', vessel.vessel_type], ['DWT', vessel.dwt],
              ['Charterer', vessel.charterer], ['Deal', vessel.deal_reference],
            ].map(([k, v]) => v && (
              <div key={k} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text3)', minWidth: 120 }}>{k}</span>
                <span style={{ color: 'var(--text1)' }}>{v}</span>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="card-title">Voyage Daten</div>
            {[
              ['Port of Loading',    vessel.port_of_loading],
              ['Port of Discharge',  vessel.port_of_discharge],
              ['Laycan',             vessel.laycan_start && vessel.laycan_end ? `${vessel.laycan_start} – ${vessel.laycan_end}` : vessel.laycan_start],
              ['ETA Loading',        vessel.eta_loading],
              ['ETD Loading',        vessel.etd_loading],
              ['ETA Discharge',      vessel.eta_discharge],
            ].map(([k, v]) => v && (
              <div key={k} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text3)', minWidth: 140 }}>{k}</span>
                <span style={{ color: 'var(--text1)' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'laytime' && (
        <div className="card">
          <div className="card-title">⚓ Laytime & Demurrage</div>
          {(() => {
            const allowed = parseFloat(vessel.laytime_allowed_hrs) || 0;
            const used    = parseFloat(vessel.laytime_used_hrs) || 0;
            const rate    = parseFloat((vessel.demurrage_rate || '').replace(/,/g, '')) || 0;
            const overage = Math.max(0, used - allowed);
            const demurrage = overage > 0 ? (overage / 24) * rate : 0;
            return (
              <div>
                <div className="grid-3" style={{ marginBottom: 16 }}>
                  {[
                    { label: 'Erlaubte Laytime', value: allowed ? `${allowed} h` : '—', color: 'var(--accent)' },
                    { label: 'Genutzte Laytime', value: used ? `${used} h` : '—', color: used > allowed ? 'var(--danger)' : 'var(--accent)' },
                    { label: 'Demurrage Rate', value: rate ? `USD ${rate.toLocaleString()}/Tag` : '—', color: 'var(--warn)' },
                  ].map(c => (
                    <div key={c.label} className="card stat-card">
                      <div className="stat-value" style={{ fontSize: 24, color: c.color }}>{c.value}</div>
                      <div className="stat-label">{c.label}</div>
                    </div>
                  ))}
                </div>
                {overage > 0 && (
                  <div style={{ padding: '12px 16px', background: '#ef444415', border: '1px solid #ef444440', borderRadius: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--danger)' }}>
                      ⚠ Demurrage: {overage.toFixed(1)} h Überschreitung = <strong>USD {demurrage.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong>
                    </div>
                  </div>
                )}
                {used <= allowed && allowed > 0 && (
                  <div style={{ padding: '12px 16px', background: '#22c55e15', border: '1px solid #22c55e40', borderRadius: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
                      ✓ Despatch: {(allowed - used).toFixed(1)} h innerhalb Laytime
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {tab === 'attachments' && (
        <div className="card">
          <div className="card-title">📎 Anhänge</div>
          <AttachmentsWidget entityType="vessel" entityId={vessel.id} />
        </div>
      )}

      {vessel.notes && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title">Notizen</div>
          <p style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{vessel.notes}</p>
        </div>
      )}
    </div>
  );
}

export default function VesselPage() {
  const [vessels, setVessels]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [q, setQ]               = useState('');
  const [stageFilter, setStage] = useState('');
  const [selected, setSelected] = useState(null);
  const [modal, setModal]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (stageFilter) params.stage = stageFilter;
      if (q) params.q = q;
      const r = await API.get('/vessels', { params });
      setVessels(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [q, stageFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Vessel "${selected.vessel_name}" wirklich löschen?`)) return;
    await API.delete(`/vessels/${selected.id}`);
    setSelected(null);
    load();
  };

  if (selected) {
    return (
      <div className="page-content">
        {modal === 'edit' && (
          <VesselModal vessel={selected} onClose={() => setModal(null)}
            onSaved={async () => {
              const r = await API.get(`/vessels/${selected.id}`);
              setSelected(r.data); load();
            }} />
        )}
        <VesselDetail vessel={selected} onBack={() => setSelected(null)}
          onEdit={() => setModal('edit')}
          onDelete={handleDelete} />
      </div>
    );
  }

  return (
    <div className="page-content">
      {modal === 'create' && (
        <VesselModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>⛴ Vessel Nominations</h1>
          <p>Schiffsnominierungen, Laytime & Voyage Tracking</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('create')}>+ Vessel nominieren</button>
      </div>

      {/* Stats */}
      {!loading && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
          {[
            { label: 'Gesamt', value: vessels.length, color: 'var(--accent)' },
            { label: 'Aktiv', value: vessels.filter(v => !['completed','cancelled'].includes(v.stage)).length, color: '#3b82f6' },
            { label: 'Abgeschlossen', value: vessels.filter(v => v.stage === 'completed').length, color: '#22c55e' },
          ].map(c => (
            <div key={c.label} className="card" style={{ minWidth: 120, textAlign: 'center', padding: '12px 20px' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input className="form-input" style={{ maxWidth: 260 }} placeholder="Suche Schiff, Deal, Charterer…"
          value={q} onChange={e => setQ(e.target.value)} />
        <select className="form-input" style={{ maxWidth: 200 }} value={stageFilter} onChange={e => setStage(e.target.value)}>
          <option value="">Alle Stages</option>
          {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load}>⟳</button>
      </div>

      {loading && <div className="loading">Lade…</div>}

      {!loading && vessels.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⛴</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Keine Vessels</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Erstelle die erste Vessel-Nominierung.</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {vessels.map(v => (
          <div key={v.id} className="card" style={{ cursor: 'pointer' }} onClick={() => setSelected(v)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 28 }}>⛴</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{v.vessel_name}</span>
                  {v.imo_number && <span style={{ fontSize: 11, color: 'var(--text3)' }}>IMO {v.imo_number}</span>}
                  <StageBadge stage={v.stage} />
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text2)', flexWrap: 'wrap' }}>
                  {v.vessel_type && <span>{v.vessel_type.toUpperCase()}</span>}
                  {v.dwt && <span>DWT: {v.dwt}</span>}
                  {v.charterer && <span>Charterer: {v.charterer}</span>}
                  {v.deal_reference && <span>Deal: {v.deal_reference}</span>}
                  {v.port_of_loading && <span>↑ {v.port_of_loading}</span>}
                  {v.port_of_discharge && <span>↓ {v.port_of_discharge}</span>}
                  {v.eta_loading && <span>ETA: {v.eta_loading}</span>}
                </div>
              </div>
              <div style={{ fontSize: 18, color: 'var(--text3)' }}>›</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
